import { ObjectId } from 'lib_ui-services';

/**
 * Give every entry of a `{relation}-transaction` its own patch-record identity.
 *
 * The server uses the update's `correlationId` as the `_id` of the `{relation}-patch` record,
 * so entries sharing one correlationId share one `_id`: the first insert wins and the rest are
 * dropped on a duplicate key, silently losing their audit history. That was GH #1299.
 *
 * Minting happens here, in `willCreate`, rather than at the point of use, because two
 * consumers have to agree on the id:
 *
 * - `doingCreate_namespace_relation_transaction` dispatches an update per entry under it, and
 *   `databaseOperations/update.js` writes the optimistic local `{relation}-patch` record under
 *   that exact `_id`.
 * - the batched wire payload carries it per entry, so a server that fans the transaction out
 *   in-process records the change under the same `_id` the client already used, instead of a
 *   second audit record describing the same change (GH #1521).
 *
 * An entry that already carries a `correlationId` keeps it. The property is appended rather
 * than prepended so entries built as `{_id, <titleAlternative>}` keep their key order.
 *
 * @param {Object[]} [entries] transaction entries, each with an `_id`
 * @returns {Object[]} the entries, each guaranteed to carry a `correlationId`
 */
export default function withEntryCorrelationIds(entries) {
    if (!Array.isArray(entries)) return entries;
    return entries.map(entry => {
        if (entry?.correlationId) return entry;
        return { ...entry, correlationId: new ObjectId().toString() };
    });
}
